import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Descriptions, Form, Input, Modal, Space, Table, Tag, Typography, message } from 'antd';
import { registrarService } from '../../services/registrarService';
import CountryFlag from '../../../shared/components/CountryFlag';

const { Text } = Typography;
const ROLE_LABEL = { registrant: '注册人', admin: '管理员', tech: '技术', billing: '账单' };

/**
 * 隐私模式域名（后台）：真实资料默认不显示。
 * 「依法披露」= 仅凭法院/执法文书，必须填理由 + 文书编号，记入审计；「滥用备注」只记录，严重滥用用「暂停」。
 */
const PrivacyModePanel = ({ domain }) => {
    const [log, setLog] = useState([]);
    const [mode, setMode] = useState(null); // 'disclose' | 'abuse'
    const [busy, setBusy] = useState(false);
    const [revealed, setRevealed] = useState(null);
    const [form] = Form.useForm();

    const loadLog = useCallback(async () => {
        try {
            const r = await registrarService.getPrivacyLog({ domain_id: domain.id });
            if (r.success) setLog(r.data || []);
        } catch (_) { /* ignore */ }
    }, [domain.id]);
    useEffect(() => { loadLog(); }, [loadLog]);

    const submit = async () => {
        const v = await form.validateFields();
        setBusy(true);
        try {
            const r = mode === 'disclose'
                ? await registrarService.disclosePrivacy({ id: domain.id, ...v })
                : await registrarService.privacyAbuseNote({ id: domain.id, note: v.note });
            if (!r.success) {
                message.error(r.error || '操作失败');
                return;
            }
            if (mode === 'disclose') setRevealed(r.data);
            else message.success('已记录');
            setMode(null);
            form.resetFields();
            loadLog();
        } catch (e) {
            message.error(e.error || e.message || '操作失败');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Space direction="vertical" style={{ width: '100%', padding: 16 }} size={16}>
            <Alert
                type="info"
                showIcon
                message="隐私模式：注册商登记的是平台代理，用户真实资料只在平台，仅凭法院/执法文书披露"
                description={domain.privacy_email ? <Text copyable>{domain.privacy_email}</Text> : null}
            />
            <Space wrap>
                <Button danger onClick={() => { setMode('disclose'); form.resetFields(); }}>依法披露</Button>
                <Button onClick={() => { setMode('abuse'); form.resetFields(); }}>滥用备注</Button>
                <Text type="secondary" style={{ fontSize: 12 }}>严重滥用请用「暂停」</Text>
            </Space>
            {log.length > 0 && (
                <Card size="small" title="披露 / 滥用记录">
                    <Table rowKey="id" size="small" pagination={false} dataSource={log} scroll={{ x: 640 }}
                        columns={[
                            { title: '时间', dataIndex: 'created_at', width: 160 },
                            { title: '类型', dataIndex: 'kind', width: 90, render: (k) => (k === 'disclosure' ? <Tag color="red">依法披露</Tag> : <Tag>滥用备注</Tag>) },
                            { title: '文书编号', dataIndex: 'order_ref', render: (v, r) => (v ? `${v}${r.authority ? ` · ${r.authority}` : ''}` : '-') },
                            { title: '理由', dataIndex: 'reason', ellipsis: true },
                            { title: '操作人', dataIndex: 'admin_name', width: 100 },
                        ]} />
                </Card>
            )}

            <Modal
                open={!!mode}
                title={mode === 'disclose' ? '依法披露真实资料' : '滥用备注'}
                okText={mode === 'disclose' ? '确认披露（记审计）' : '保存'}
                okButtonProps={{ danger: mode === 'disclose', loading: busy }}
                onOk={submit}
                onCancel={() => setMode(null)}
                destroyOnClose
            >
                <Form form={form} layout="vertical" requiredMark={false}>
                    {mode === 'disclose' ? (
                        <>
                            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="仅限法院 / 执法机关命令。滥用投诉转给用户，不在这里披露" />
                            <Form.Item name="order_ref" label="文书编号" rules={[{ required: true, message: '请填写文书编号' }]}><Input /></Form.Item>
                            <Form.Item name="authority" label="发令机关"><Input /></Form.Item>
                            <Form.Item name="reason" label="理由" rules={[{ required: true, min: 10, message: '至少 10 个字' }]}><Input.TextArea rows={3} /></Form.Item>
                        </>
                    ) : (
                        <Form.Item name="note" label="备注" rules={[{ required: true, message: '请填写备注' }]}><Input.TextArea rows={3} /></Form.Item>
                    )}
                </Form>
            </Modal>

            <Modal open={!!revealed} title="已披露（本次已记审计）" footer={<Button onClick={() => setRevealed(null)}>关闭</Button>} onCancel={() => setRevealed(null)} width={640}>
                {revealed?.account && <Text type="secondary">账号 #{revealed.account.id} · {revealed.account.username} · {revealed.account.email}</Text>}
                {revealed && Object.entries(revealed.contacts || {}).map(([role, c]) => (
                    role !== 'registrant' && c.same_as_registrant ? null : (
                        <Descriptions key={role} size="small" column={1} bordered title={ROLE_LABEL[role] || role} style={{ marginTop: 12 }}>
                            <Descriptions.Item label="姓名">{`${c.first_name || ''} ${c.last_name || ''}`}</Descriptions.Item>
                            {c.company && <Descriptions.Item label="公司">{c.company}</Descriptions.Item>}
                            <Descriptions.Item label="邮箱">{c.email}</Descriptions.Item>
                            <Descriptions.Item label="电话">{c.phone}</Descriptions.Item>
                            <Descriptions.Item label="地址"><CountryFlag code={c.country} />{[c.address1, c.address2, c.city, c.state, c.postal_code, c.country].filter(Boolean).join(', ')}</Descriptions.Item>
                        </Descriptions>
                    )
                ))}
            </Modal>
        </Space>
    );
};

export default PrivacyModePanel;
